import { AnalyticsBrowser } from '@segment/analytics-next'
import useContextStore from '@/stores/context'
import useModulesStore from '@/stores/modules'
import useCdcProps from '@/composables/useCdcProps'
import useUrlSearchParams from '@/composables/useUrlSearchParams'
import { getAccountsEmailFromJwt } from '@/utils/jwt'

import type { Callback } from '@segment/analytics-next'
import type { Module } from '@/types/modules.interfaces'
interface TrackOpts {
  properties?: object
  options?: object
  callback?: Callback
}

interface GroupOpts {
  groupId?: string
  traits?: object
  options?: object
  callback?: Callback
}

const analytics = AnalyticsBrowser.load({
  writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY,
})

export default () => {
  const getTrackingUserId = () => {
    return (
      analytics.instance?.user().id() ||
      analytics.instance?.user().anonymousId()
    )
  }

  const page = async (name: string, properties = {}) => {
    const { context } = useCdcProps()
    const { getUrlSearchParams } = await useUrlSearchParams()
    const source = await getUrlSearchParams('utm_mbo_source')

    await launchOrWaitIdentify()

    analytics.page(`${name} Viewed`, {
      ps_version: context.shop_version,
      mbo_version: context.mbo_version,
      shop_url: context.shop_url,
      source: source ? source : undefined,
      module_updates_available: context?.upgradable_modules?.length,
      email_account: getAccountsEmailFromJwt(context.accounts_token),
      ...properties,
    })
  }

  const launchOrWaitIdentify = () => {
    return new Promise((resolve) => {
      const { context, xprops } = useCdcProps()
      if (context.accounts_user_id) {
        xprops
          .handleAnalytics(launchIdentify)
          .then(
            ({ loadAnalytics, firstLaunchIdentity, loadedAnalytics }: any) => {
              if (loadedAnalytics) resolve(true)
              firstLaunchIdentity(loadAnalytics).then(() => resolve(true))
            },
          )
      } else {
        resolve(true)
      }
    })
  }

  const launchIdentify = (loadAnalytics: boolean) => {
    new Promise((resolve) => {
      if (loadAnalytics) {
        const { context, xprops } = useCdcProps()
        analytics.identify(context.accounts_user_id).then(() => {
          xprops.handleAnalyticsLoaded(launchIdentify)
          resolve(true)
        })
        group()
      }
    })
  }

  const track = (event: string, trackOpts: TrackOpts = {}) => {
    const { context } = useCdcProps()
    const contextStore = useContextStore()
    const { properties = {}, options = {}, callback } = trackOpts

    analytics.track(
      event,
      {
        source_name: 'MBO',
        ps_version: context.shop_version,
        mbo_version: context.mbo_version,
        shop_url: context.shop_url,
        email_account: getAccountsEmailFromJwt(context.accounts_token),
        shop_creation_date: context.shop_creation_date
          ? new Date(context.shop_creation_date).toISOString()
          : null,
        algolia_query_ID: contextStore.algoliaContext.queryID,
        algolia_index: contextStore.algoliaContext.index,
        ...properties,
      },
      {
        context: {
          groupId: context.accountsShopId,
        },
        ...options,
      },
      callback,
    )
  }

  const group = (groupOpts: GroupOpts = {}) => {
    const context = useContextStore()
    const modules = useModulesStore()

    const {
      groupId = context.accountsShopId,
      traits = {},
      options = {},
      callback,
    } = groupOpts

    const defaultTraits = {
      groupId: context.accountsShopId,
      modules: Object.keys(modules.installed),
      ps_version: context.shopVersion,
      mbo_version: context.mboVersion,
      shop_url: context.shopUrl,
      shop_creation_date: context.shopCreationDate?.toISOString() || null,
    }

    analytics.group(
      groupId,
      { ...defaultTraits, ...traits },
      { ...options },
      callback,
    )
  }

  const getModuleProperties = (module: Module) => ({
    module_category: module.categoryId,
    module_id: module.id,
    module_installed: module.installed,
    module_name: module.name,
    module_purchased: module.purchased || false,
    module_category_parent_name: module.categoryParentEnglishName,
    module_category_parent_id: module.categoryParentId,
    module_display_name: module.displayName,
    module_category_name: module.categoryName,
    module_price: module.price.EUR,
    module_rate: module.avgRate,
  })

  return { group, page, track, getModuleProperties, getTrackingUserId }
}
